import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBox from "../../AdminComponents/Common/SearchBox";
import PaginationDataTable from "../../AdminComponents/Common/PaginationDataTable";
import Heading from "../../AdminComponents/Common/Heading";
import CustomButton from "../../Components/Common/CustomButton";
import axios from "axios";
import { BASE_URL } from "../../Assets/Constants";
import { toast } from "react-toastify";
import { theme } from "../../Assets/Theme/theme";

function AdminExperience() {
  const navigate = useNavigate();
  const columns = [
    { id: "S.no", name: "S.no" },
    { id: "name", name: "Title" },
    { id: "country_city", name: "Location" },
    { id: "action", name: "Action" },
  ];
  const [rows, setRows] = useState([]);
  const [page, pageChange] = useState(0);
  const [rowPerPage] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const loginData = JSON.parse(localStorage.getItem("adminData"));
  const token = loginData?.token;

  const fetchData = async () => {
    setLoading(true)
    try {
      const response = await axios.get(`${BASE_URL}/admin/experiences`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const rowsData = Object.values(response.data.data || {});

      setRows(rowsData);
      setFilteredData(rowsData);
    } catch (error) {
      if (error.response?.status === 401 || error.message.includes("Authentication")) {
        navigate("/login");
      } 
      console.error("Error fetching experiences:", error);
    } finally {
      setLoading(false)
    }
  };
  
  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenModal = (id) => {
    setSelectedId(id);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedId(null);
  };

  const onDelete = async () => {
    if (!token) {
      toast.error("Authentication token is missing", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      navigate('/login')
      return;
    }
    try {
      const response = await axios.delete(
        `${BASE_URL}/admin/experience/${selectedId}/delete`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setRows((prevRows) => prevRows.filter((row) => row.id !== selectedId));
      toast.success(response?.data?.message, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        style: { backgroundColor: 'orange', color: 'white' }, 
      });
      fetchData()
    } catch (error) {
      console.error("Error deleting experience:", error);
      toast.error("Failed to delete experience. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    } finally {
      handleCloseModal();
    }
  };

  const onEdit = async (experienceId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/admin/experience/${experienceId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const experienceDetails = response.data;
      navigate("/admin/experience/edit", { state: experienceDetails?.data });
    } catch (error) {
      console.error("Error fetching experience details:", error);
      if (error.response?.status === 401 || error.message.includes("Authentication")) {
        navigate("/login");
      } else {
      toast.error("Failed to fetch experience details. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    }
    }
  };

  const [currentPage, setCurrentPage] = useState(0); 

  const handleSearchChange = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchQuery(value);
    setCurrentPage(0); 
    const filtered = rows.filter((item) =>
      item.name?.toLowerCase().includes(value)
    );

    setFilteredData(filtered);
  };

  return (
    <Box>
      <Heading label={"Experiences"} />
      <Box
        mt={4}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <SearchBox
          placeholder={"Search by title"}
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <Box width={"fit-content"} mx={{ xs: "auto", md: 0 }}>
          <CustomButton onClick={() => navigate("/admin/experience/add")}>
            Add Experience
          </CustomButton>
        </Box>
      </Box>

      <Box>
      {loading ? (
          <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh", 
          }}
        >
          <CircularProgress size={30} sx={{ color: theme.colors.orange }} />
        </Box>
        ) :  (
          <PaginationDataTable
            editAndDelete={true}
            columns={columns}
            rows={filteredData}
            onEdit={(experience) => onEdit(experience._id)}
            onDelete={(experience) => handleOpenModal(experience._id)}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        
        )}
      </Box>

      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this experience?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="black" sx={{
              backgroundColor: theme.colors.orange,
              borderRadius: '5px',
              color: 'white',
              '&:hover': {
                backgroundColor: 'darkorange',
              },
            }}>
            Cancel
          </Button>
          <Button onClick={onDelete} color="primary" sx={{
              backgroundColor: theme.colors.red,
              borderRadius: '5px',
              color: 'white',
              '&:hover': {
                backgroundColor: '#FF474C',
              },
            }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminExperience;
